/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from "@tanstack/react-table";
import { isResubmitted } from "../../utils/resubmission";
import { DataTableColumnHeader } from "../shared/data-table-column-header";
import { NsuDataTableRowActions } from "./nsu-data-table-row-actions";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusWithResubmission } from "../shared/status-with-resubmission";
import { NsuStatusEnum } from "@/enum/nsu-status";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { NSU_RESUBMISSION_BADGE_TEST_ID } from "../../utils/case-history-test-ids";
import { CLASSIFICATION_LABELS } from "../../utils/messages";
import { ClassificationBadge } from "../divergence/classification-badge";

export const useNsuColumns = (): ColumnDef<any>[] => {
  const navigate = useNavigate();
  const location = useLocation();

  return useMemo(() => [
    {
      id: "select",
      header: ({ table }) => {
        const rows = table.getRowModel().rows;
        const selectableRows = rows.filter(row => {
          const status = row.original.status;
          return status === NsuStatusEnum.PENDING || status === NsuStatusEnum.ACTIVE;
        });

        if (selectableRows.length === 0) {
          return (
            <Checkbox
              checked={false}
              disabled
              aria-label="Nenhum item selecionável"
            />
          );
        }

        const allSelectableSelected = selectableRows.every(row => row.getIsSelected());
        const someSelectableSelected = selectableRows.some(row => row.getIsSelected());

        return (
          <Checkbox
            checked={allSelectableSelected || (someSelectableSelected && "indeterminate")}
            onCheckedChange={(value) => {
              selectableRows.forEach(row => row.toggleSelected(!!value));
            }}
            aria-label="Selecionar todos"
          />
        );
      },
      cell: ({ row }) => {
        const status = row.original.status;
        const canSelect = status === NsuStatusEnum.PENDING || status === NsuStatusEnum.ACTIVE;
        return canSelect ? (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Selecionar linha"
          />
        ) : (
          <div className="flex items-center justify-center w-4 h-4 rounded border border-gray-300 bg-gray-100">
            <X className="h-3 w-3 text-gray-400" />
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "conciliation_date",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Data de Conciliação" />
      ),
      cell: ({ row }) => {
        const date = row.original.conciliation?.date;
        if (!date) return "-";

        const formattedDate = new Date(date).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
        return formattedDate;
      },
    },
    {
      accessorKey: "store_code",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Loja" />
      ),
      cell: ({ row }) => {
        return row.original.conciliation?.store?.code || "-";
      },
    },
    {
      accessorKey: "number",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="NSU" />
      ),
      cell: ({ row }) => {
        return row.original.number || "-";
      },
    },
    {
      accessorKey: "operator",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Operador" />
      ),
      cell: ({ row }) => {
        return row.original.operator || "-";
      },
    },
    {
      accessorKey: "amount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Valor" />
      ),
      cell: ({ row }) => {
        const amount = parseFloat(row.original.amount);
        const formattedAmount = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(amount);

        return (
          <span className={amount < 0 ? 'text-red-600' : 'text-green-600'}>
            {formattedAmount}
          </span>
        );
      },
    },
    {
      id: "analyst",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Conferente" />
      ),
      cell: ({ row }) => {
        return row.original.conciliation?.store?.responsible?.name || "-";
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => (
        <StatusWithResubmission
          status={row.original.status}
          resubmitted={isResubmitted(row.original)}
          resubmissionTestId={NSU_RESUBMISSION_BADGE_TEST_ID}
        />
      ),
    },
    {
      id: "is_loss",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={CLASSIFICATION_LABELS.COLUMN_TITLE} />
      ),
      cell: ({ row }) => (
        <ClassificationBadge divergence={{ is_loss: row.original.is_loss }} />
      ),
    },
    {
      id: "actions",
      header: "Ações",
      cell: ({ row }) => <NsuDataTableRowActions row={row} />,
    },
    {
      id: "conciliation_link",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Conciliação" />
      ),
      cell: ({ row }) => {
        const conciliationId = row.original.conciliation_id ?? row.original.conciliation?.id;

        if (!conciliationId) return "-";

        const path = `/conciliacao/lojas/conciliacao-de-caixa-de-loja/${conciliationId}`;

        return (
          <button
            type="button"
            onClick={() => navigate(path, { state: { from: location.pathname } })}
            className="text-[#5577b9] hover:underline"
          >
            Conciliar
          </button>
        );
      },
      enableSorting: false,
    },
  ], [navigate, location.pathname]);
};
